import './SurveyItem.css';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import PersonIcon from '@mui/icons-material/Person';
import { useNavigate } from 'react-router-dom';
import ResultFilter from '../ResultFilter/ResultFilter';

function convertDate(date) {
  try {
    if (!date) return '';

    const d =
      typeof date === 'string' || typeof date === 'number'
        ? new Date(date)
        : date;

    if (isNaN(d?.getTime?.())) return '';

    return format(d, 'dd/MM/yyyy');
  } catch {
    return '';
  }
}

function normalizeMongoDate(value) {
  if (!value) return null;

  if (typeof value === 'string') return value;

  if (typeof value === 'object' && value.$date) return value.$date;

  return null;
}

function truncateEmail(email) {
  if (!email) return '—';

  const MAX = 32;

  return email.length > MAX
    ? `${email.slice(0, MAX)}...`
    : email;
}

function SurveyItem({
  data,
  enterpriseName,
  bce,
  sector,
  sectorCode,
  currentUser,
  handleEditProfile,
  handleCompareSession,
  handleShareSession,
  handleModifySession
}) {

  const sessions = Array.isArray(data) ? data : [];

  const { t } = useTranslation();
  const navigate = useNavigate();

  function convertStatusText(statusTag) {
    if (statusTag === 'completed') return t('survey.list.completed');
    if (statusTag === 'not-started') return t('survey.list.not-started');
    if (statusTag === 'in-progress') return t('survey.list.started');
    return '';
  }

  function onOptionChange(_, key, sessionId, sharedWith) {

    switch (key) {

      case 'result':
        navigate(`result/${sessionId}`);
        break;

      case 'modifyProfile':
        handleEditProfile(bce, sectorCode, sessionId);
        break;

      case 'compare':
        handleCompareSession(sessionId, enterpriseName);
        break;

      case 'share':
        handleShareSession(sessionId, enterpriseName, sharedWith);
        break;

      case 'modifySurvey':
      case 'continue':
        handleModifySession(sessionId, sectorCode);
        break;

      default:
        break;
    }
  }

  return (
    <div className="survey-cards">

      {sessions.map((item) => {

        const respondentEmail =
          (item?.user?.email || item?.userEmail || '').trim() || '—';

        const isOwner =
          respondentEmail !== '—' && currentUser?.email
            ? currentUser.email.toLowerCase() === respondentEmail.toLowerCase()
            : false;

        const statusText = convertStatusText(item.status);

        const namespaceLabel = item.namespace
          ? `${t('survey.list.questionnaire')} ${
              item.namespace.includes('25')
                ? '2025'
                : item.namespace.includes('22')
                ? '2022'
                : item.namespace.includes('20')
                ? '2020'
                : '—'
            }`
          : t('survey.list.survey2020');

        return (
          <article
            className="survey-card"
            key={item._id}
          >

            <header className="survey-card__header">
              <h3 className="survey-card__title">
                {enterpriseName}
              </h3>

              <div className="survey-card__bce">
                N° BCE {bce}
                <span className="survey-card__sep">/</span>
                {sector}
              </div>
            </header>

            <div className="survey-card__row">

              <div className="survey-card__cell">
                <div className="survey-card__label">
                  {t('survey.list.version')}
                </div>

                <div className="survey-card__value">
                  {namespaceLabel}
                </div>
              </div>

              <div className="survey-card__cell">
                <div className="survey-card__label">
                  {t('survey.list.start')}
                </div>

                <div className="survey-card__value">
                  {convertDate(
                    normalizeMongoDate(
                      item.updatedAt ||
                      item.UpdatedAt ||
                      ''
                    )
                  )}
                </div>
              </div>

              <div className="survey-card__cell">
                <div className="survey-card__label">
                  {t('survey.list.updated')}
                </div>

                <div className="survey-card__value">
                  {convertDate(
                    normalizeMongoDate(
                      item.updatedAt ||
                      item.UpdatedAt ||
                      item.updated ||
                      ''
                    )
                  )}
                </div>
              </div>

              <div className="survey-card__cell">
                <div className="survey-card__label">
                  {t('survey.list.status')}
                </div>

                <div className="survey-card__status">

                  {item.status === 'completed' ? (
                    <img
                      src="/true_bubble.svg"
                      alt=""
                      className="status-icon"
                    />
                  ) : item.status === 'in-progress' ? (
                    <span
                      className="status-icon status-icon--progress"
                      aria-hidden="true"
                    />
                  ) : (
                    <span
                      className="status-icon status-icon--notstarted"
                      aria-hidden="true"
                    />
                  )}

                  <span>{statusText}</span>

                </div>
              </div>

              <div className="survey-card__cell card__cell__respondent">

                <div className="survey-card__label">
                  {t('survey.list.respondent')}
                </div>

                <div className="survey-card__value respondent">

                  {isOwner && (
                    <PersonIcon fontSize="small" />
                  )}

                  <img
                    src="/icon-respondant.svg"
                    alt=""
                    className="respondent-icon"
                  />

                  <span title={respondentEmail}>
                    {truncateEmail(respondentEmail)}
                  </span>

                </div>

              </div>

              <div className="survey-card__actions">

                <ResultFilter
                  name={t('survey.list.button')}
                  item
                  options={t(
                    `survey.list.${
                      item.status === 'completed'
                        ? new Date(
                            new Date().setDate(
                              new Date(item.completedAt).getDate() + 15
                            )
                          ) < new Date()
                          ? 'completedExpiredOptions'
                          : 'completedOptions'
                        : 'inProgressOptions'
                    }`,
                    { returnObjects: true }
                  )}
                  onOptionChange={(_, key) =>
                    onOptionChange(
                      _,
                      key,
                      item?._id,
                      item?.sharedWith
                    )
                  }
                />

              </div>

            </div>

          </article>
        );

      })}

    </div>
  );
}

export default SurveyItem;